<template>
  <div :class="inline ? '' : 'text-center mt-2'" v-if="breakdown && breakdown.length">
    <a href="#" class="compiled-weights-toggle" @click.prevent="expanded = !expanded">
      {{ expanded ? '▲ Hide weights' : '▼ Show where this score comes from' }}
    </a>
    <div v-if="expanded" class="compiled-weights-list mt-2" :class="{ 'compiled-weights-list--start': inline }">
      <span v-if="coverageNote" class="compiled-weight-chip">{{ coverageNote }}</span>
      <span
        v-for="item in breakdown"
        :key="item.metric"
        class="compiled-weight-chip"
      >{{ formatMetricLabel(item.metric) }}: <template v-if="!weightsOnly">{{ item.normalized }}/100 &times; </template>{{ item.weight }} pts</span>
    </div>
  </div>
</template>

<script>
// The Combined Score weight breakdown, shared by the Individual Driver page
// (driver_manager_individual.vue) and the Roster List page
// (driver_manager_roster_list.vue). The weights themselves come from the tenant's
// scorecard_compiled_weights and are identical for every driver, so the Roster List
// renders one page-level expand (weightsOnly) rather than a per-row one; only the
// per-driver `normalized` subscore and the coverage note vary by driver.
const METRIC_LABELS = {
  risk_score: 'Safety Score',
  speeding_percentage: 'Speeding %',
  idling_percentage: 'Idling %',
  following_distance_per_10k_miles: 'Following Dist.',
  harsh_braking_per_10k_miles: 'Harsh Braking / 10k',
  distracted_driving_percentage: 'Distracted / 10k',
  weekend_availability_percentage: 'Wknd Avail.',
  customer_requests_per_10k_miles: 'Cust. Req. / 10k',
  kudos_per_10k_miles: 'Kudos / 10k',
  dvir_completion_rate: 'DVIRs / (Bus Days × 2)',
}

export default {
  name: 'driver_manager_score_breakdown',
  props: {
    // [{ metric, weight, normalized }] — straight off a driver row's compiled_score_breakdown
    breakdown: { type: Array, default: () => [] },
    // when set, drop the per-driver half of each chip and the coverage note, leaving
    // just "Label: N pts" — the tenant-wide formula
    weightsOnly: { type: Boolean, default: false },
    // the driver row the breakdown came from; only read for the coverage note
    driver: { type: Object, default: null },
    // drop the centered standalone line and sit inline in a host toolbar row
    inline: { type: Boolean, default: false },
  },
  data() {
    return { expanded: false }
  },
  computed: {
    // the host's driver object is a fresh identity after every refetch, so watching
    // the object itself would collapse the panel whenever the date range changed.
    // key off the identity that actually means "a different driver".
    driverKey() {
      if (!this.driver) return null
      return this.driver.driver_id || this.driver.driver_name || null
    },
    coverageNote() {
      if (this.weightsOnly || !this.driver) return ''
      const covered = this.driver.compiled_score_weight_covered
      const total = this.driver.compiled_score_weight_total
      if (covered == null || total == null || covered >= total) return ''
      return `Based on ${covered} of ${total} weight points. Some KPIs had no data this period.`
    },
  },
  watch: {
    // a new driver selection collapses the panel, matching the old page-local reset
    driverKey() {
      this.expanded = false
    },
  },
  methods: {
    formatMetricLabel(metric) {
      return METRIC_LABELS[metric] || metric
    },
  },
}
</script>

<style scoped>
/* class names are load-bearing: midnight.css re-themes them by name under
   .fi-hz-root .dsd-root, which both host pages render inside */
.compiled-weights-toggle {
  font-size: 13px;
  color: #888;
  text-decoration: none;
}
.compiled-weights-toggle:hover { color: #555; }
.compiled-weights-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}
.compiled-weights-list--start { justify-content: flex-start; }
.compiled-weight-chip {
  background: #f1f3f5;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 13px;
  color: #555;
}
</style>
